import { Button, FilledInput, InputAdornment, Stack } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Api } from '../../api'
import { GoodsService, GoodsType } from '../../services/goods'
import { Fa } from '../Fa'
import { Form, FormProps } from '../Form'

const PromoCodeField: FC<PromoCodeFieldProps> = ({ goods, onApply }) => {
  const { t } = useTranslation()

  const [discounted, setDiscounted] = useState<boolean>()

  const goodsRef = useRef(goods)

  const handleSubmit = useCallback<FormProps['onSubmit']>(
    async (fields) => {
      try {
        setDiscounted(undefined)
        const value = fields['promo-code']

        if (!value) {
          onApply()
          return
        }

        const discounts = await Api.post('promo_code/prices', {
          code: value,
          items: [
            {
              type: GoodsService.getType(goods),
              id: goods.id,
              price: GoodsService.getRoughPrice(goods),
            },
          ],
        })
        const discountedPrice = discounts[0].discounted_price

        if (typeof discountedPrice === 'undefined') {
          setDiscounted(false)
          onApply()
          return
        }

        setDiscounted(true)
        onApply({ code: value, discountedPrice })
      } catch (error) {
        toast.error((error as Error).message)
      }
    },
    [goods, onApply]
  )

  useEffect(() => {
    if (goods === goodsRef.current) {
      return
    }

    goodsRef.current = goods
    setDiscounted(undefined)
    onApply()
  }, [goods, onApply])

  return (
    <Form onSubmit={handleSubmit}>
      <Stack
        key={JSON.stringify(goods)}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <FilledInput
          name="promo-code"
          placeholder="Promo code"
          slotProps={{ input: { size: 16 } }}
          {...(discounted
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    {<Fa name="check" color="success" size="lg" />}
                  </InputAdornment>
                ),
              }
            : discounted === false
            ? {
                color: 'error',
                endAdornment: (
                  <InputAdornment position="end">
                    {<Fa name="xmark" color="error" size="lg" />}
                  </InputAdornment>
                ),
              }
            : undefined)}
        />
        <Button type="submit" variant="outlined">
          {t('Apply')}
        </Button>
      </Stack>
    </Form>
  )
}

type PromoCodeFieldProps = {
  goods: GoodsType
  onApply: (promo?: Promo) => void
}

type Promo = {
  code: string
  discountedPrice: number
}

export { PromoCodeField }
export type { Promo, PromoCodeFieldProps }
